import React, { useEffect, useState } from "react";
import { FiMapPin } from "react-icons/fi";
import {
    Plug, Wrench, TriangleAlert, Fuel, Thermometer, BatteryWarning,
    Gauge, OctagonAlert, Zap, ShieldAlert, BatteryPlus,
} from "lucide-react";
import useVehicleStateDIC from "../../../hook/useVehicleStateDIC";
import ChartLoader from "../../Dashboard/Charginganalysis/ChartLoader";
import evIcon from "../../../assets/img/ev-icon.png";

const DASH = "-";
const show = (val) => (val === undefined || val === null || val === "" ? DASH : val);

const STABLE = "#c9ced6";
const WARNING = "#f5a623";
const CRITICAL = "#e2434d";

const HEALTH_ICONS = [
    { label: "HV Charge Indication", name: "hv_charge_indication", Icon: Plug },
    { label: "Service Indication", name: "service_indication", Icon: Wrench },
    { label: "EV Fault Indication", name: "ev_fault_indication", Icon: TriangleAlert },
    { label: "Coolant low", name: "coolant_low", Icon: Fuel },
    { label: "Charging Fault", name: "charging_fault", Icon: null, text: "FAULT" },
    { label: "High Temperature indication", name: "high_temperature_indication", Icon: Thermometer },
    { label: "LV battery charging fault", name: "lv_battery_below_set_limit", Icon: BatteryWarning },
    { label: "Low SOC Warning", name: "low_soc_warning", Icon: Gauge },
    { label: "Stop Vehicle", name: "stop_vehicle", Icon: OctagonAlert },
    { label: "DC to DC convert Fault", name: "dc_dc_fault", Icon: Zap },
    { label: "Electric Hazard", name: "electric_hazard", Icon: ShieldAlert },
    { label: "Battery Error", name: "hv_battery_error", Icon: BatteryPlus },
    // "Vehicle Immobilize" removed: no corresponding field/state exists in the CVP telltales_data API response.
    // { label: "Vehicle Immobilize", name: "", Icon: Ban },
];

// Information panel removed: none of these fields (Engine No, Fuel Type, Vehicle Type, Branch,
// Laden/Unladen Weight, Tonnage, Specifications, Registration Date, Make, Vehicle Application,
// Owned Outside, Vehicle Code, Registration Address) are present in the CVP vehicle-state API response.
// const INFO_FIELDS = [
//     ["Engine No", "engineNo"], ["Fuel Type", "fuelType"], ["Vehicle Type", "vehicleType"],
//     ["Branch", "branch"], ["Laden Weight", "ladenWeight"], ["Unladen Weight", "unladenWeight"],
//     ["Tonnage", "tonnage"], ["Specifications", "specifications"], ["Registration Date", "registrationDate"],
//     ["Make", "make"], ["Vehicle Application", "vehicleApplication"], ["Owned Outside", "ownedOutside"],
//     ["Vehicle Code", "vehicleCode"], ["Registration Address", "registrationAddress"],
// ];

function statusColor(value) {
    if (value === undefined || value === null) return STABLE;
    return value === 0 ? STABLE : value === 1 ? WARNING : CRITICAL;
}

const cardStyle = {
    border: "1px solid #2f8fd6",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    position: "relative",
    backgroundColor: "#fff",
};

const dividerStyle = {
    borderLeft: "1px solid #9aa4b2",
};

const panelStyle = {
    border: "1px solid #9aa4b2",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    backgroundColor: "#fff",
};

function VehicleStateCard_Coral({ gloablfilter }) {
    const { fetchVehicleState, isLoading } = useVehicleStateDIC();
    const [vehicleState, setVehicleState] = useState(null);
    const [telltales, setTelltales] = useState([]);

    useEffect(() => {
        const vin = gloablfilter?.VIN_NUM?.[0];
        if (!vin) return;

        const applyData = (result) => {
            setVehicleState(result?.vehicle_state?.[0] || null);
            setTelltales(result?.telltales_data || []);
        };

        fetchVehicleState(vin, applyData);
        const timeInterval = setInterval(() => fetchVehicleState(vin, applyData), 300000);
        return () => clearInterval(timeInterval);
    }, [gloablfilter]);

    const telltaleValue = (name) => {
        const found = telltales.find((t) => t.name === name);
        return found ? found.value : undefined;
    };

    const isLive = !!vehicleState;
    const soc = vehicleState?.batterySoc?.[0];
    const vin = show(vehicleState?.vehicleId);
    const mapUrl = (vehicleState?.gpsLatitude !== undefined && vehicleState?.gpsLongitude !== undefined)
        ? `https://www.google.com/maps?q=${vehicleState.gpsLatitude},${vehicleState.gpsLongitude}`
        : null;

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center w-100">
                <ChartLoader />
            </div>
        );
    }

    return (
        <>
            <div className="p-3 mb-3" style={cardStyle}>
                <div className="row g-3">
                    <div className="col-12 col-xl-3">
                        {isLive && (
                            <div className="mb-2">
                                <span
                                    className="fw-bold d-inline-block"
                                    style={{
                                        backgroundColor: CRITICAL, color: "#fff", border: `1px solid ${CRITICAL}`,
                                        borderRadius: "6px", padding: "4px 14px", fontSize: "0.75rem",
                                    }}
                                >
                                    Live
                                </span>
                            </div>
                        )}
                        <div className="d-flex align-items-start ps-1">
                            <img
                                src={evIcon}
                                alt="EV"
                                className="me-4 flex-shrink-0"
                                style={{ width: "100px", height: "100px", borderRadius: "10px", objectFit: "cover" }}
                            />

                            <div>
                                <a href="#" onClick={(e) => e.preventDefault()} className="fw-bold text-decoration-none" style={{ color: "#2f6fed" }}>
                                    {vin}
                                </a>
                                <div className="d-flex mt-1 mb-2" style={{ gap: "6px" }}>
                                    <span className="border rounded px-2 py-1" style={{ fontSize: "0.75rem", fontWeight: 600 }}>TATA</span>
                                    <span className="border rounded px-2 py-1 fst-italic" style={{ fontSize: "0.75rem", color: "#2e9e5b" }}>Electric</span>
                                </div>
                                {/* Driver info removed: no driver field exists in the CVP vehicle-state API response. */}
                                {/* <div className="text-muted mb-1" style={{ fontSize: "0.85rem" }}>
                                    Driver:&nbsp;<span className="fw-semibold text-body">NA</span>&nbsp;|&nbsp;<span className="fw-semibold text-body">NA</span>
                                </div> */}
                                <div className="mb-2">
                                    <a
                                        href={mapUrl || "#"}
                                        target={mapUrl ? "_blank" : undefined}
                                        rel={mapUrl ? "noopener noreferrer" : undefined}
                                        onClick={(e) => { if (!mapUrl) e.preventDefault(); }}
                                        className="text-decoration-none d-inline-flex align-items-center"
                                        style={{ color: "#2f6fed", fontSize: "0.9rem" }}
                                    >
                                        <FiMapPin size={14} className="me-1" /> View Location
                                    </a>
                                </div>
                                {/* "Immobilise" removed: no immobilize control/state field exists in the CVP vehicle-state API response. */}
                                {/* <button
                                    type="button"
                                    className="btn btn-dark d-inline-flex align-items-center"
                                    style={{ fontSize: "0.85rem", borderRadius: "6px" }}
                                    disabled
                                >
                                    <FiLock size={14} className="me-2" /> Immobilise
                                </button> */}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-4 ps-xl-4" style={{ ...dividerStyle }}>
                        <div className="fw-bold mb-2" style={{ fontSize: "1.05rem" }}>Distance to Empty: {show(vehicleState?.distanceToEmpty)}</div>
                        <div className="d-flex justify-content-between align-items-baseline">
                            <span>Charge Status</span>
                            <span className="fw-bold">{show(soc)}{soc !== undefined ? "%" : ""}</span>
                        </div>
                        <div className="progress mb-2" style={{ height: "6px", backgroundColor: "#e9ecef" }}>
                            <div className="progress-bar" style={{ width: `${soc || 0}%`, backgroundColor: "#2e9e5b" }} />
                        </div>
                        <div className="d-flex align-items-center mb-1">
                            <Zap size={14} className="me-1" />
                            <span className="fw-bold">{show(vehicleState?.is_charging_status)}</span>
                        </div>
                        <div className="fst-italic text-muted mb-2" style={{ fontSize: "0.9rem" }}>{show(vehicleState?.timeToCharge)}</div>
                        <div>Battery Temperature: <span className="fw-bold">{show(vehicleState?.max_battery_temperature)}{vehicleState?.max_battery_temperature !== undefined ? "°C" : ""}</span></div>
                        <div>Motor Temperature: <span className="fw-bold">{show(vehicleState?.motor_temperature)}{vehicleState?.motor_temperature !== undefined ? "°C" : ""}</span></div>
                        <div>State Of Health: <span className="fw-bold">{show(vehicleState?.stateOfHealth)}</span></div>
                    </div>

                    <div className="col-12 col-xl-5 ps-xl-4" style={{ ...dividerStyle }}>
                        <div className="row">
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Model No:</div>
                                <div className="fw-bold">{show(vehicleState?.PL)}</div>
                            </div>
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Odometer (kms)</div>
                                <div className="fw-bold">{show(vehicleState?.odometer)}</div>
                            </div>
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>VIN No. / Chassis No.:</div>
                                <div className="fw-bold">{vin}</div>
                            </div>
                            {/* Engine Hrs to Service removed: no engineHrsToService field exists in the CVP vehicle-state API response. */}
                            {/* <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Engine Hrs to Service (Hrs)</div>
                                <div className="fw-bold">{show(vehicleState?.engineHrsToService)}</div>
                            </div> */}
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Current Speed (Km/hr)</div>
                                <div className="fw-bold">{show(vehicleState?.speed)}</div>
                            </div>
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Trip A - Energy Efficiency</div>
                                <div className="fw-bold">{show(vehicleState?.trip_a_efficiency)}</div>
                            </div>
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Trip B - Energy Efficiency</div>
                                <div className="fw-bold">{show(vehicleState?.trip_b_efficiency)}</div>
                            </div>
                            <div className="col-4 mb-3">
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>Motor RPM</div>
                                <div className="fw-bold">{show(vehicleState?.motor_rpm)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Information panel removed: no fields in the CVP vehicle-state API populate it. */}
                <div className="col-12 mb-3">
                    <div className="p-3" style={panelStyle}>
                        <div className="d-flex align-items-center mb-4">
                            <h6 className="fw-bold mb-0 me-4" style={{ color: "#2f6fed" }}>Vehicle Health</h6>
                            <span className="d-inline-flex align-items-center me-3" style={{ fontSize: "0.85rem" }}>
                                <span className="d-inline-block rounded-circle me-1" style={{ width: "10px", height: "10px", backgroundColor: STABLE }}></span>Stable
                            </span>
                            <span className="d-inline-flex align-items-center me-3" style={{ fontSize: "0.85rem" }}>
                                <span className="d-inline-block rounded-circle me-1" style={{ width: "10px", height: "10px", backgroundColor: WARNING }}></span>Warning
                            </span>
                            <span className="d-inline-flex align-items-center" style={{ fontSize: "0.85rem" }}>
                                <span className="d-inline-block rounded-circle me-1" style={{ width: "10px", height: "10px", backgroundColor: CRITICAL }}></span>Critical
                            </span>
                        </div>
                        <div className="row">
                            {HEALTH_ICONS.map((item, idx) => {
                                const color = statusColor(telltaleValue(item.name));
                                const Icon = item.Icon;
                                return (
                                    <div key={idx} className="col-2 text-center mb-4">
                                        <div
                                            className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                                            style={{ width: "56px", height: "56px", backgroundColor: color + "26", color }}
                                        >
                                            {Icon ? <Icon size={24} /> : <span style={{ fontSize: "0.6rem", fontWeight: "bold" }}>{item.text}</span>}
                                        </div>
                                        <div style={{ fontSize: "0.78rem" }} className="text-muted">{item.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default VehicleStateCard_Coral;
